import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInternationalization, useNotification } from "../../../../../lib/contexts";
import * as API from "../../../rooms";
import { roomDetailsKey, roomListKey } from "../room-query-keys";

export function useMutationUnlinkIntegration() {
  const queryClient = useQueryClient();
  const { t } = useInternationalization("pages.rooms.alerts.unlink-integration");
  const { success, error: errorAlert } = useNotification();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: ({
      roomId,
      integrationType,
      keepFutureBookings,
    }: {
      roomId: string;
      integrationType: API.IntegrationType;
      keepFutureBookings: boolean;
    }) =>
      API.unlinkIntegration(roomId, {
        integrationType,
        keepFutureBookings,
      }),
    onSuccess: (_data, variables) => {
      success(t("success"));
      queryClient.invalidateQueries({ queryKey: roomListKey() });
      queryClient.invalidateQueries({ queryKey: roomDetailsKey(variables.roomId) });
      // Invalidate rooms with integrations for all users (we don't have ownerId here)
      queryClient.invalidateQueries({ queryKey: ["rooms", "with-integrations"] });
    },
    onError: (error, variables) => {
      console.error("Error unlinking integration:", error);
      errorAlert(t("error"));
      queryClient.invalidateQueries({ queryKey: roomDetailsKey(variables.roomId) });
    },
  });

  return {
    unlinkIntegration: mutate,
    unlinkIntegrationAsync: mutateAsync,
    isPending,
    isError,
    error,
  };
}

export function useMutationUnlinkIntegrationDeleteAll() {
  const queryClient = useQueryClient();
  const { t } = useInternationalization("pages.rooms.alerts.unlink-integration");
  const { success, error: errorAlert } = useNotification();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: ({
      roomId,
      integrationType,
    }: {
      roomId: string;
      integrationType: API.IntegrationType;
    }) =>
      API.unlinkIntegrationDeleteAll(roomId, {
        integrationType,
      }),
    onSuccess: (_data, variables) => {
      success(t("success"));
      queryClient.invalidateQueries({ queryKey: roomListKey() });
      queryClient.invalidateQueries({ queryKey: roomDetailsKey(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: ["rooms", "with-integrations"] });
    },
    onError: (error, variables) => {
      console.error("Error unlinking integration and deleting all bookings:", error);
      errorAlert(t("error"));
      queryClient.invalidateQueries({ queryKey: roomDetailsKey(variables.roomId) });
    },
  });

  return {
    unlinkIntegrationDeleteAll: mutate,
    unlinkIntegrationDeleteAllAsync: mutateAsync,
    isPending,
    isError,
    error,
  };
}
